/**
 * Normalize: the opt-in tidy-up of stored values (the server's `/items/bulk/normalize`). Values are compared by their
 * keys either way; this only rewrites how they look, each formatted or typed value in its standard form. It counts
 * first, saying how many values it would rewrite and how many it can't read (they stay as written), with a few
 * examples; then makes the change, one history entry. Every item of a filter runs in the background, as a job.
 */
import { useEffect, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemFilter, Job, Normalized, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { ignore } from "../../lib/ignore.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";

/** How many of the examples the preview shows. */
const EXAMPLES = 5;

/** Which items: these, or every one a search matches. */
export type NormalizeTarget = { ids: readonly string[] } | { filter: ItemFilter };

/** A value as the preview shows it (text as it is, anything else as JSON). */
const shown = (v: unknown) => (typeof v === "string" ? v : JSON.stringify(v));

export function NormalizeDialog(p: {
  space: SpaceOut;
  open: boolean;
  onClose: () => void;
  title: string;
  target: NormalizeTarget;
  /** Only these fields (keys). */
  fields?: readonly string[] | undefined;
  /** Done at once (by ids), or started in the background (a filter's every match). */
  onDone: (r: Normalized | Job) => void;
}) {
  const [preview, setPreview] = useState<Normalized | null>(null);
  const path = spaceApi(p.space, "/items/bulk/normalize");
  const body = { ...p.target, ...(p.fields && { fields: p.fields }) };
  const key = JSON.stringify(body);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the request's body.
  useEffect(() => {
    if (!p.open) {
      setPreview(null);
      return;
    }
    const ctl = new AbortController();
    api
      .post<Normalized>(path, { ...body, dry_run: true }, { signal: ctl.signal })
      .then(setPreview)
      .catch(ignore);
    return () => ctl.abort();
  }, [p.open, key]);
  const [run, busy] = useAction(async () => {
    const r = await api.post<Normalized | Job>(path, body);
    p.onClose();
    p.onDone(r);
  });
  return (
    <Dialog open={p.open} onClose={p.onClose} title={p.title}>
      <p class="text-sm text-zinc-600">{t("normalize.what")}</p>
      {preview === null ? (
        <p class="text-sm text-zinc-500">{t("normalize.counting")}</p>
      ) : preview.rewritten === 0 ? (
        <p class="text-sm text-zinc-700">{t("normalize.nothing")}</p>
      ) : (
        <div class="flex flex-col gap-2 text-sm text-zinc-800">
          <p>
            {t("normalize.rewritten", { count: preview.rewritten })}
            {preview.unreadable > 0 ? `; ${t("normalize.unreadable", { count: preview.unreadable })}` : null}
          </p>
          <div>
            <p class="text-xs text-zinc-500">{t("normalize.example")}</p>
            <ul class="flex flex-col gap-0.5 font-mono text-xs">
              {preview.sample.slice(0, EXAMPLES).map((x) => (
                <li key={`${x.item}:${x.field}:${shown(x.from)}`}>{`${shown(x.from)} → ${shown(x.to)}`}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          answer="confirm"
          variant="primary"
          busy={busy}
          disabled={!preview || preview.rewritten === 0}
          onClick={() => run()}
        >
          {t("normalize.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
